import {
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
  createUpdater,
} from '../../../../srcCommon/helpers/updateHelpers';
import {AnswerModel, ApiAnswer} from '../../../models/api/answerModel';
import {serializeAttachment, UploadedAttachmentModel} from '../../../models/api/attachmentModel';
import {postApiAsync, putApiAsync} from '../../../services/http/http';
import {HttpResponseError} from '../../../services/http/httpError';
import {findAnswerLock} from '../../../services/locks/resourceLocks';
import {ThunkAsyncAction} from '../../appThunk';
import {optimisticUpdateAsyncAction} from '../../optimisticUpdates/optimisticUpdatesActions';
import {getCompany} from '../company/companySelectors';
import {updateDataAction} from '../dataGroupsActions';
import {getOriginalData} from '../dataSelectors';
import {DataState} from '../dataState';
import {AnswerActionsEnum, AnswerNameAlreadyExistsError, AnswerUpdatedAction} from './answerActions';

export interface AnswerTemplate {
  name: string;
  subject: string;
  body: string;
  namespace: string;
  showInAllInboxes: boolean;
  inboxIds: ReadonlyArray<number>;
  attachments: ReadonlyArray<UploadedAttachmentModel>;
  folderId?: number;
}

export function createAnswerFromTemplateAsyncAction(template: AnswerTemplate): ThunkAsyncAction<AnswerModel> {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);

    const url = `${company.url}/canned_answers`;
    const body = {
      namespace: template.namespace,
      name: template.name,
      subject: template.subject,
      body: template.body,
      attachments: template.attachments.map((a) => serializeAttachment(a)),
      all_inboxes: template.showInAllInboxes,
      inbox_ids: template.inboxIds,
      folder: template.folderId ? {id: template.folderId} : undefined,
    };

    try {
      const answer = await postApiAsync(ApiAnswer, url, body);
      dispatch<AnswerUpdatedAction>({
        payload: answer,
        type: AnswerActionsEnum.ANSWER_UPDATED,
      });

      return answer;
    } catch (error) {
      if (!(error instanceof HttpResponseError)) {
        throw error;
      }

      switch (error.status) {
        case 409:
          throw new AnswerNameAlreadyExistsError();
        default:
          throw error;
      }
    }
  };
}

export function saveAnswerAsyncAction(answerId: number, template: AnswerTemplate): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const optimisticUpdater = createObjectPropertyUpdater<DataState, 'answers'>(
      'answers',
      createObjectPropertyUpdater(
        'byId',
        createResourceMapValueUpdater(
          answerId,
          createUpdater<AnswerModel>((answerFolder) => ({
            ...answerFolder,
            name: template.name,
            subject: template.subject,
            body: template.body,
            namespace: template.namespace,
            showInAllInboxes: template.showInAllInboxes,
            inboxIds: template.inboxIds,
            attachments: template.attachments,
            folderId: template.folderId,
          })),
        ),
      ),
    );

    await dispatch(
      optimisticUpdateAsyncAction(optimisticUpdater, async () =>
        findAnswerLock(answerId).lockAsync(async () => {
          const state = getState();
          const {answers} = getOriginalData(state);
          const answer = answers.byId[answerId];

          if (!answer) {
            return undefined;
          }

          try {
            const body = {
              namespace: template.namespace,
              name: template.name,
              subject: template.subject,
              body: template.body,
              attachments: template.attachments.map((a) => serializeAttachment(a)),
              all_inboxes: template.showInAllInboxes,
              inbox_ids: template.inboxIds,
              folder: template.folderId ? {id: template.folderId} : undefined,
            };
            const newAnswer = await putApiAsync(ApiAnswer, answer.url, body);

            return updateDataAction({answers: [newAnswer]});
          } catch (error) {
            if (!(error instanceof HttpResponseError)) {
              throw error;
            }

            switch (error.status) {
              case 409:
                throw new AnswerNameAlreadyExistsError();
              default:
                throw error;
            }
          }
        }),
      ),
    );
  };
}
